/**
 * ReportForm Component
 *
 * Form for adding/editing reports.
 */
import { useState, useCallback, useEffect } from 'react';
import { Modal, FormField } from '../ui';
import type { Client, Report, ReportFormData } from '../../types';

interface ReportFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ReportFormData) => Promise<void>;
  report?: Report | null;
  clients: Client[];
  selectedClientId?: string | null;
  isLoading?: boolean;
}

const initialFormData: ReportFormData = {
  name: '',
  iframeSrc: '',
  order: 0,
  clientId: '',
};

export const ReportForm = ({
  isOpen,
  onClose,
  onSubmit,
  report,
  clients,
  selectedClientId,
  isLoading = false,
}: ReportFormProps) => {
  const [formData, setFormData] = useState<ReportFormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isEditMode = !!report;

  // Reset form when modal opens/closes or report changes
  useEffect(() => {
    if (isOpen) {
      if (report) {
        setFormData({
          name: report.name,
          iframeSrc: report.iframeSrc || '',
          order: report.order,
          clientId: report.clientId,
        });
      } else {
        setFormData({
          ...initialFormData,
          clientId: selectedClientId || '',
        });
      }
      setErrors({});
    }
  }, [isOpen, report, selectedClientId]);

  const validate = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.iframeSrc.trim()) {
      newErrors.iframeSrc = 'Power BI URL is required';
    } else {
      try {
        new URL(formData.iframeSrc);
      } catch {
        newErrors.iframeSrc = 'Invalid URL format';
      }
    }

    if (!formData.clientId) {
      newErrors.clientId = 'Client is required';
    }

    if (formData.order < 0) {
      newErrors.order = 'Order must be non-negative';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    await onSubmit(formData);
  };

  const handleChange = (field: keyof ReportFormData, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? 'Edit Report' : 'Add Report'}
      size="lg"
    >
      <form onSubmit={handleSubmit}>
        <FormField
          label="Name"
          value={formData.name}
          onChange={(e) => handleChange('name', e.target.value)}
          error={errors.name}
          required
          placeholder="Enter report name"
          disabled={isLoading}
        />

        <FormField
          label="Power BI Embed URL"
          value={formData.iframeSrc}
          onChange={(e) => handleChange('iframeSrc', e.target.value)}
          error={errors.iframeSrc}
          required
          placeholder="https://app.powerbi.com/reportEmbed?..."
          disabled={isLoading}
          helperText="Paste the iframe src URL from Power BI"
        />

        <FormField label="Client" error={errors.clientId} required>
          <select
            value={formData.clientId}
            onChange={(e) => handleChange('clientId', e.target.value)}
            disabled={isLoading || isEditMode}
            className={`
              w-full px-3 py-2 text-sm
              border rounded-lg
              focus:outline-none focus:ring-2 focus:ring-offset-0
              ${errors.clientId
                ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                : 'border-gray-300 focus:ring-metrica-green focus:border-metrica-green'
              }
              disabled:bg-gray-100 disabled:cursor-not-allowed
            `}
          >
            <option value="">Select a client</option>
            {clients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </select>
        </FormField>

        <FormField
          label="Order"
          type="number"
          min={0}
          value={String(formData.order)}
          onChange={(e) => handleChange('order', parseInt(e.target.value) || 0)}
          error={errors.order}
          disabled={isLoading}
          helperText="Position in the sidebar (0 = first)"
        />

        {/* Preview */}
        {formData.iframeSrc && !errors.iframeSrc && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Preview
            </label>
            <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-100 h-48">
              <iframe
                src={formData.iframeSrc}
                title="Report Preview"
                className="w-full h-full"
                sandbox="allow-scripts allow-same-origin"
              />
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-metrica-green rounded-lg hover:bg-metrica-green/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-metrica-green disabled:opacity-50"
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Saving...
              </span>
            ) : isEditMode ? (
              'Update Report'
            ) : (
              'Create Report'
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default ReportForm;
